import type {
	AgentType,
	FieldRule,
	FixedValue,
	RefCast,
} from "@productlane/lib"
import { AGENT_TYPES, castTextTo } from "@productlane/lib"

import type { JsonKind, SchemaField } from "./tool-schema-fields"

/** Which control edits the value. Derived from the tool's own schema. */
export type ValueEditorKind =
	| "text"
	| "number"
	| "boolean"
	| "null"
	| "enumSelect"
	| "scalarList"
	| "json"

export const CONTAINER_KINDS: readonly JsonKind[] = ["object", "array"]

export function isContainerKind(kind: JsonKind): boolean {
	return CONTAINER_KINDS.includes(kind)
}

export function kindLabel(kind: JsonKind): string {
	return kind === "integer" ? "whole number" : kind
}

/** The JSON kind of anything, containers included. */
export function jsonKindOf(value: unknown): JsonKind {
	if (Array.isArray(value)) return "array"
	if (value === null) return "null"
	if (typeof value === "object") return "object"
	if (typeof value === "boolean") return "boolean"
	if (typeof value === "number") return "number"
	return "string"
}

/** The kind a stored value actually is, so a mismatch can be shown rather than hidden. */
export function valueKind(value: FixedValue): JsonKind {
	return jsonKindOf(value)
}

/** The kind a pinned rule holds, whichever of the two modes stores it. */
export function storedKindOf(
	rule: Extract<FieldRule, { mode: "fixed" | "json" }>,
): JsonKind {
	return jsonKindOf(rule.value)
}

/**
 * The kind to edit as. A single declared kind decides it; anything wider falls back
 * to a string, which is what every field did before types were read.
 */
export function defaultKindFor(field: SchemaField): JsonKind {
	if (field.enumValues?.length) return valueKind(field.enumValues[0]!)
	if (field.kinds.length === 1) return field.kinds[0]!
	if (field.kinds.length === 0) return "string"
	return field.kinds.includes("string") ? "string" : field.kinds[0]!
}

export function editorKindFor(
	field: SchemaField,
	kind: JsonKind,
): ValueEditorKind {
	if (field.enumValues?.length) return "enumSelect"
	switch (kind) {
		case "object":
			return "json"
		case "array":
			// A list of objects needs raw JSON; a list of scalars gets the token field.
			return field.itemKinds?.some(isContainerKind) ? "json" : "scalarList"
		case "boolean":
			return "boolean"
		case "null":
			return "null"
		case "number":
		case "integer":
			return "number"
		default:
			return "text"
	}
}

/** The rule mode a pinned value of this kind is stored under. */
export function fixedModeFor(kind: JsonKind): "fixed" | "json" {
	return isContainerKind(kind) ? "json" : "fixed"
}

/**
 * The blank rule a kind starts from. Matches editorKindFor: a list of scalars is
 * a fixed token list, a list of containers is raw JSON.
 */
export function emptyRuleFor(kind: JsonKind, field?: SchemaField): FieldRule {
	if (kind === "object") return { mode: "json", value: {} }
	if (kind === "array") {
		return field?.itemKinds?.some(isContainerKind)
			? { mode: "json", value: [] }
			: { mode: "fixed", value: [] }
	}
	if (kind === "number" || kind === "integer")
		return { mode: "fixed", value: 0 }
	if (kind === "boolean") return { mode: "fixed", value: false }
	if (kind === "null") return { mode: "fixed", value: null }
	return { mode: "fixed", value: "" }
}

/**
 * Every token type, with the ones the schema declares first. All six stay
 * reachable: a narrowing the schema does not ask for is still a real guard, and
 * `integer` on a `number` argument is the common one.
 */
export function agentTypesFor(field: SchemaField): AgentType[] {
	const declared = field.kinds.map((kind) =>
		isContainerKind(kind) ? "json" : (kind as AgentType),
	)
	// An integer argument is a number too, so the wider name stays next to it.
	if (declared.includes("integer")) declared.push("number")
	if (declared.includes("number")) declared.push("integer")
	if (field.nullable) declared.push("null")
	return [...new Set([...declared, ...AGENT_TYPES])]
}

/** Preselected so a ref on a typed field injects that type without extra setup. */
export function castForField(field: SchemaField): RefCast | undefined {
	if (field.kinds.includes("string")) return undefined
	// A container can only come from text by parsing it, so a template that writes
	// JSON is the one way a reference can fill an object or a list.
	if (field.kinds.length > 0 && field.kinds.every(isContainerKind))
		return "json"
	if (field.kinds.includes("boolean")) return "boolean"
	if (field.kinds.includes("number") || field.kinds.includes("integer")) {
		return "number"
	}
	return undefined
}

/** A ref or template rule that would send text where the tool wants a container. */
export function needsJsonCast(
	field: SchemaField,
	rule: { mode: string; cast?: RefCast; [key: string]: unknown },
): boolean {
	if (rule.mode !== "ref" && rule.mode !== "template") return false
	if (rule.cast === "json") return false
	return field.kinds.length > 0 && field.kinds.every(isContainerKind)
}

export type ScalarParse =
	| { ok: true; value: string | number | boolean | null }
	| { ok: false; error: string }

export function parseTypedScalar(raw: string, kind: JsonKind): ScalarParse {
	switch (kind) {
		case "number":
		case "integer": {
			const parsed = castTextTo(raw, kind)
			if (parsed.ok) return parsed
			// A number that is merely not whole gets the sharper of the two messages.
			return castTextTo(raw, "number").ok
				? { ok: false, error: `"${raw}" is not a whole number.` }
				: { ok: false, error: `"${raw}" is not a number.` }
		}
		case "boolean": {
			const parsed = castTextTo(raw, "boolean")
			return parsed.ok
				? parsed
				: { ok: false, error: `"${raw}" is not true or false.` }
		}
		case "null": {
			const parsed = castTextTo(raw, "null")
			return parsed.ok ? parsed : { ok: false, error: `"${raw}" is not null.` }
		}
		default:
			return { ok: true, value: raw }
	}
}

/** A value typed for the list's declared element kind. */
export function parseListEntry(
	raw: string,
	itemKinds: JsonKind[] | undefined,
):
	| { ok: true; value: string | number | boolean }
	| { ok: false; error: string } {
	const trimmed = raw.trim()
	if (!trimmed) return { ok: false, error: "Enter a value." }

	const kind = itemKinds?.length === 1 ? itemKinds[0]! : "string"
	const parsed = parseTypedScalar(trimmed, kind)
	if (!parsed.ok) return parsed
	if (parsed.value === null) return { ok: false, error: "Enter a value." }
	return { ok: true, value: parsed.value }
}

/**
 * Whether a stored value already satisfies the schema. valueKind never reports
 * `integer`, so a whole number counts for an integer field and a fraction does not.
 */
export function isValueCompatible(
	value: FixedValue,
	field: SchemaField,
): boolean {
	if (field.kinds.length === 0) return true
	const stored = valueKind(value)
	if (stored === "null") return field.nullable || field.kinds.includes("null")
	if (field.kinds.includes(stored)) return true
	if (stored === "number" && field.kinds.includes("integer")) {
		return Number.isInteger(value)
	}
	return false
}

/** A lossless retype of a stored value, for the mismatch notice's fix button. */
export function recoerceFixedValue(
	value: FixedValue,
	kind: JsonKind,
): { ok: true; value: FixedValue } | { ok: false } {
	if (valueKind(value) === kind) return { ok: true, value }
	if (Array.isArray(value)) return { ok: false }

	const parsed = parseTypedScalar(String(value ?? ""), kind)
	return parsed.ok ? { ok: true, value: parsed.value } : { ok: false }
}

export function fixedRuleValue(rule: FieldRule): FixedValue | null {
	return rule.mode === "fixed" ? rule.value : null
}
